import { useEffect } from "react";
import { isStoredMediaUrl, cachedImageUrl, resolveImageUrl } from "../lib/storageUrl";

/**
 * Photos uploaded through the CMS live in a private storage bucket, so the URL
 * saved on the record cannot be loaded directly. This watcher swaps any such
 * <img> src for a signed URL as soon as the image appears in the page, so
 * uploads preview in the dashboard and display on the public site without
 * every component having to know about storage.
 */
const swap = (img) => {
  const src = img.getAttribute("src");
  if (!src || !isStoredMediaUrl(src)) return;

  const ready = cachedImageUrl(src);
  if (ready) {
    if (img.src !== ready) img.src = ready;
    return;
  }

  img.dataset.storageSrc = src;
  resolveImageUrl(src).then((signed) => {
    if (signed && img.isConnected) img.src = signed;
  });
};

const scan = (root) => {
  if (!root || root.nodeType !== 1) return;
  if (root.tagName === "IMG") swap(root);
  root.querySelectorAll?.("img").forEach(swap);
};

export default function StorageImageResolver() {
  useEffect(() => {
    scan(document.body);

    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === "attributes" && record.target.tagName === "IMG") {
          swap(record.target);
          return;
        }
        record.addedNodes.forEach(scan);
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src"],
    });

    return () => observer.disconnect();
  }, []);

  return null;
}
